// Lokale Datei-Backups via File System Access API (Chrome/Edge)
// Speichert verschlüsselte .amedBackup-Dateien in einem vom Nutzer gewählten Ordner.

import { exportDexieEncrypted } from './backupService';

const IDB_NAME   = 'amed-fs-handles';
const IDB_STORE  = 'handles';
const HANDLE_KEY = 'local-backup-dir';
const MAX_LOCAL  = 7;

// ── Handle-Persistenz (eigene unverschlüsselte IDB, kein med. Inhalt) ────────

function openHandleDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = (): void => { req.result.createObjectStore(IDB_STORE); };
    req.onsuccess       = (): void  => { resolve(req.result); };
    req.onerror         = (): void  => { reject(req.error); };
  });
}

async function saveHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  const db = await openHandleDb();
  await new Promise<void>((resolve, reject) => {
    const tx  = db.transaction(IDB_STORE, 'readwrite');
    const req = tx.objectStore(IDB_STORE).put(handle, HANDLE_KEY);
    req.onsuccess = (): void => { resolve(); };
    req.onerror   = (): void => { reject(req.error); };
  });
  db.close();
}

async function loadHandle(): Promise<FileSystemDirectoryHandle | null> {
  const db = await openHandleDb();
  return new Promise<FileSystemDirectoryHandle | null>((resolve, reject) => {
    const tx  = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(HANDLE_KEY);
    req.onsuccess = (): void => {
      db.close();
      resolve((req.result as FileSystemDirectoryHandle | undefined) ?? null);
    };
    req.onerror = (): void => { db.close(); reject(req.error); };
  });
}

// ── Hilfs-Funktionen ─────────────────────────────────────────────────────────

type FSHandleWithPerms = FileSystemDirectoryHandle & {
  queryPermission: (opts: { mode: string }) => Promise<string>;
};

async function hasPermission(handle: FileSystemDirectoryHandle): Promise<boolean> {
  try {
    const perm = await (handle as FSHandleWithPerms).queryPermission({ mode: 'readwrite' });
    return perm === 'granted';
  } catch {
    return false;
  }
}

type FSDirectoryHandleIterable = FileSystemDirectoryHandle & {
  entries: () => AsyncIterableIterator<[string, FileSystemHandle]>;
  removeEntry: (name: string) => Promise<void>;
};

async function rotateLocalBackups(dirHandle: FileSystemDirectoryHandle): Promise<void> {
  const names: string[] = [];
  const iterable = dirHandle as FSDirectoryHandleIterable;
  for await (const [name, entry] of iterable.entries()) {
    if (entry.kind === 'file' && name.endsWith('.amedBackup')) {
      names.push(name);
    }
  }
  names.sort();
  const toDelete = names.slice(0, Math.max(0, names.length - MAX_LOCAL + 1));
  for (const n of toDelete) {
    try { await (dirHandle as FSDirectoryHandleIterable).removeEntry(n); } catch { /* skip */ }
  }
}

// ── Öffentliche API ──────────────────────────────────────────────────────────

export function isLocalBackupSupported(): boolean {
  return 'showDirectoryPicker' in window;
}

export async function requestLocalBackupFolder(): Promise<string> {
  if (!isLocalBackupSupported()) throw new Error('Nur in Chrome/Edge unterstützt.');
  const handle = await (window as unknown as { showDirectoryPicker: (o: object) => Promise<FileSystemDirectoryHandle> })
    .showDirectoryPicker({ mode: 'readwrite', id: 'amed-backup' });
  await saveHandle(handle);
  return handle.name;
}

export async function getLocalFolderName(): Promise<string | null> {
  try {
    const handle = await loadHandle();
    return handle?.name ?? null;
  } catch {
    return null;
  }
}

export async function writeLocalBackup(): Promise<void> {
  const dirHandle = await loadHandle();
  if (dirHandle === null) return;

  const ok = await hasPermission(dirHandle);
  if (!ok) return; // requestPermission braucht User-Gesture, hier nicht verfügbar

  await rotateLocalBackups(dirHandle);

  const backupFile = await exportDexieEncrypted();
  const ts         = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
  const fileName   = `amed-backup-${ts}.amedBackup`;
  const content    = JSON.stringify(backupFile);

  const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
  const writable   = await fileHandle.createWritable();
  await writable.write(content);
  await writable.close();
}
